import i18next from 'i18next';
import { noop } from 'lodash';
import * as csTranslations from './cs.json';
import * as daTranslations from './da.json';
import * as deTranslations from './de.json';
import * as enTranslations from './en.json';
import * as fiTranslations from './fi.json';
import * as frTranslations from './fr.json';
import * as itTranslations from './it.json';
import * as jaJPTranslations from './ja.json';
import * as nbTranslations from './nb.json';
import * as nlTranslations from './nl.json';
import * as plTranslations from './pl.json';
import * as roTranslations from './ro.json';
import * as svTranslations from './sv.json';

/**
 * Translation value: either a display string or an object with message + optional description.
 * Descriptions are used by Smartling (or other TMS) to give context to translators;
 * only "message" is used at runtime.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export type TranslationValue = { string: string; 'translation-note'?: string };
export interface TranslationResource {
  [key: string]: TranslationValue | TranslationResource;
}
export interface FlattenedTranslationResource {
  [key: string]: string | FlattenedTranslationResource;
}

function isTranslationValue(obj: unknown): obj is TranslationValue {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    typeof (obj as TranslationValue).string === 'string'
  );
}
function isTranslationResource(obj: unknown): obj is TranslationResource {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}
/**
 * Recursively flatten translation resources so i18next only sees display strings.
 * Entries in the form { string: "...", 'translation-note'?: "..." } are reduced to the string;
 * plain strings are REMOVED. This allows en.json (and others) to add description
 * context for translators without changing runtime behavior.
 */
export function flattenTranslations(
  obj: TranslationResource
): FlattenedTranslationResource {
  const result: FlattenedTranslationResource = {};
  for (const [key, val] of Object.entries(obj)) {
    if (isTranslationValue(val)) {
      result[key] = val.string;
    } else if (isTranslationResource(val)) {
      result[key] = flattenTranslations(val);
    }
  }
  return result;
}

i18next.use({
  type: 'logger',
  log: noop,
  warn: noop,
  error: noop,
});

const { smartling: _smartling, ...enTranslationsWithoutSmartlingConfig } =
  enTranslations;

export const sharedTranslationResources = {
  en: {
    translation: flattenTranslations(enTranslationsWithoutSmartlingConfig),
  },
  cs: {
    translation: csTranslations,
  },
  da: {
    translation: daTranslations,
  },
  de: {
    translation: deTranslations,
  },
  fi: {
    translation: fiTranslations,
  },
  fr: {
    translation: frTranslations,
  },
  it: {
    translation: itTranslations,
  },
  ja: {
    translation: jaJPTranslations,
  },
  nb: {
    translation: nbTranslations,
  },
  nl: {
    translation: nlTranslations,
  },
  pl: {
    translation: plTranslations,
  },
  ro: {
    translation: roTranslations,
  },
  sv: {
    translation: svTranslations,
  },
};

export const i18nInstance = i18next.init({
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  resources: sharedTranslationResources,
});

export function t(key: string, options?: Record<string, unknown>): string {
  return i18next.t(key, options);
}

/**
 * Change the current language.
 * @param language The language code (e.g., 'en', 'de')
 * @returns A promise that resolves when the language has been changed
 */
export function changeLanguage(language: string): Promise<void> {
  return i18next.changeLanguage(language).then((): void => undefined);
}
